import { OPORD_TEMPLATE_ID } from 'components/DisasterGPT/constants/opordSectionDefaults';
import generateUUID from 'utils/sharedUtils/generateUUID';

export const DEFAULT_ASSET_RADIUS_KM = 25;
export const MIN_ASSET_RADIUS_KM = 1;
export const MAX_ASSET_RADIUS_KM = 500;

type BuildDraftSitrepPayloadParams = {
  scopeName: string;
  aorId?: string | null;
  incidentId?: string;
  // Sitrep template to seed sections from.
  templateId: string;
  // When true (and no useful template), create with no sections. Kept for
  // callers that pass through CreateDraftReportSelection.
  skipDefaultSections?: boolean;
  /**
   * Asset report: the asset's `resource_set_id`, plus the circle radius used
   * for point assets.
   */
  asset?: { id: string; radiusKm?: number };
  /** Custom-area report: the drawn polygon. `scopeName` is the area's name. */
  customArea?: { spatialFilter: Record<string, unknown> };
};

export function draftReportNameForTemplate(
  templateId: string,
  scopeName: string
): string {
  if (templateId === OPORD_TEMPLATE_ID) {
    return `OPORD: ${scopeName}`;
  }
  return `Report: ${scopeName}`;
}

/**
 * A radius in km within 1-500, or null when the input is missing, not a number,
 * or out of range.
 */
export function parseAssetRadiusKm(raw: unknown): number | null {
  const value =
    typeof raw === 'number' ? raw : Number(String(raw ?? '').trim());
  if (raw === '' || raw == null || !Number.isFinite(value)) return null;
  if (value < MIN_ASSET_RADIUS_KM || value > MAX_ASSET_RADIUS_KM) return null;
  return value;
}

const subjectFields = ({
  scopeName,
  asset,
  customArea,
}: Pick<
  BuildDraftSitrepPayloadParams,
  'scopeName' | 'asset' | 'customArea'
>) => {
  if (asset) {
    return {
      subject_kind: 'asset' as const,
      asset_id: asset.id,
      asset_radius_km: asset.radiusKm ?? DEFAULT_ASSET_RADIUS_KM,
    };
  }
  if (customArea) {
    return {
      subject_kind: 'custom' as const,
      spatial_filter: customArea.spatialFilter,
      spatial_filter_label: scopeName,
    };
  }
  return {};
};

export const buildDraftSitrepPayload = ({
  scopeName,
  aorId,
  incidentId,
  templateId,
  skipDefaultSections = false,
  asset,
  customArea,
}: BuildDraftSitrepPayloadParams) => ({
  skip_default_sections: templateId ? false : skipDefaultSections,
  sitrep_template_id: templateId,
  sitrep_source: 'reports',
  sitrep_source_id: generateUUID(),
  name: draftReportNameForTemplate(templateId, scopeName),
  description: '',
  selectedDatetime: new Date().toISOString(),
  aor_id: aorId || undefined,
  incident_id: asset || customArea ? undefined : incidentId || undefined,
  is_sitrep: true,
  ...subjectFields({ scopeName, asset, customArea }),
});
